import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { usePlatform } from "@/lib/platform";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion, AnimatePresence } from "framer-motion";
import {
  Stethoscope, HeartPulse, ShieldCheck, Sparkles, Activity,
  Users, ClipboardList, Pill, BookOpen, TrendingUp, DollarSign, AlertTriangle,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

// Two role profiles — one is highlighted at a time.
const PROFILES = {
  provider: {
    label: "For Clinicians",
    heading: "Multi-Specialty Command Center",
    tagline: "One console across 9 specialties — sign in as any clinician to see the same patients through your own clinical lens.",
    accent: "#60A5FA",
    icon: Stethoscope,
    bullets: [
      { icon: Users,            title: "Triage inbox",         detail: "Patients ranked by clinical risk — HIGH, MEDIUM, LOW at a glance." },
      { icon: AlertTriangle,    title: "Rapid decline alerts", detail: "eGFR drops, ACR spikes, KFRE ≥ 20%, fluid overload — flagged same-day." },
      { icon: DollarSign,       title: "RPM billing auto-log", detail: "CPT 99457 / 99458 minutes captured as you review charts." },
      { icon: ClipboardList,    title: "Audit-ready timeline", detail: "Every clinical change stamped with actor, time, before → after." },
    ],
    accounts: [
      { label: "Dr. Priya Patel — Nephrology",  email: "provider@renalos.demo", tag: "Nephrology" },
      { label: "Dr. Ramirez — Cardiology",      email: "cardio@nucleus.demo",   tag: "Cardiology" },
      { label: "Dr. Okafor — Neurology",        email: "neuro@nucleus.demo",    tag: "Neurology" },
      { label: "Dr. Kim — Oncology",            email: "onco@nucleus.demo",     tag: "Oncology" },
    ],
    cta: "Sign in to the console",
    smallPrint: "Provider access. Requires an active clinician invitation.",
  },
  patient: {
    label: "For Patients",
    heading: "Your care companion",
    tagline: "Understand your labs in plain English, track your progress, and stay ahead of every specialty in your care plan.",
    accent: "#4ADE80",
    icon: HeartPulse,
    bullets: [
      { icon: Activity,   title: "Lab tracker + scorecards", detail: "Upload a photo of any lab. Get a plain-English breakdown, color-coded." },
      { icon: Pill,       title: "Medication safety",        detail: "Every drug checked against your conditions before you take it." },
      { icon: BookOpen,   title: "Diet optimizer",           detail: "Real-time budgets tailored to your specialty targets." },
      { icon: TrendingUp, title: "Digital Twin trajectory",  detail: "Long-range projection of your health — and how lifestyle shifts it." },
    ],
    accounts: [
      { label: "Alex Morgan — Stage 3a",  email: "patient@renalos.demo",  tag: "Patient" },
      { label: "Sarah Chen — Stage 4",    email: "patient2@renalos.demo", tag: "Patient · HIGH risk" },
    ],
    cta: "Open my health dashboard",
    smallPrint: "Personal health data. HIPAA-grade encryption; only you and your clinicians can see this.",
  },
};

export default function Login() {
  const { login, user } = useAuth();
  const { branding } = usePlatform();
  const platformName = branding?.name || "Med Nexus";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialRole = searchParams.get("role") === "provider" ? "provider" : "patient";
  const [role, setRole] = useState(initialRole);
  const profile = PROFILES[role];

  const [email, setEmail] = useState(PROFILES[initialRole].accounts[0].email);
  const [password, setPassword] = useState("demo1234");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const swapRole = (next) => {
    setRole(next);
    setEmail(PROFILES[next].accounts[0].email);
    setError("");
  };

  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");

  const [directory, setDirectory] = useState({ departments: [], doctors_by_department: {} });
  const [selectedDept, setSelectedDept] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/platform/directory");
        setDirectory(data || { departments: [], doctors_by_department: {} });
      } catch { /* silent */ }
    })();
  }, []);
  const doctorsForSelected = useMemo(
    () => (selectedDept ? (directory.doctors_by_department[selectedDept] || []) : []),
    [directory, selectedDept]
  );

  if (user) {
    return <Navigate to={user.role === "provider" ? "/provider" : "/"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), password, mfaCode || undefined);
      navigate(u.role === "provider" ? "/provider" : "/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail?.code || (typeof detail === "object" ? detail?.code : null);
      if (code === "MFA_REQUIRED") {
        setMfaRequired(true);
        setError("");
      } else if (code === "MFA_INVALID") {
        setError("Invalid MFA code — try the current 6-digit code from your app.");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = profile.icon;

  const onQuickPick = (val) => {
    if (!val) return;
    setEmail(val);
    setPassword("demo1234");
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-[#080A09] text-white" data-testid="login-page">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle variant="icon" />
      </div>

      <div className="h-full w-full grid lg:grid-cols-[1.1fr_1fr]">
        {/* Left hero — full-height, no scroll */}
        <aside
          data-testid={`login-hero-${role}`}
          className="hidden lg:flex flex-col justify-between px-12 xl:px-16 py-10 relative overflow-hidden"
          style={{
            background: "#0B0F0D",
            "--aurora-c1": role === "provider" ? "#60A5FA" : "#4ADE80",
            "--aurora-c2": role === "provider" ? "#A78BFA" : "#22D3EE",
            "--aurora-c3": role === "provider" ? "#22D3EE" : "#A78BFA",
          }}
        >
          {/* Animated aurora backdrop — sits below content */}
          <div className="login-aurora" aria-hidden="true">
            <span className="login-aurora-core" />
          </div>

          {/* Brand */}
          <div className="relative z-10 flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-[#4ADE80] flex items-center justify-center ros-glow-primary">
              <span className="text-black font-heading font-bold text-xl">{platformName.charAt(0)}</span>
            </div>
            <div>
              <div className="font-heading text-2xl tracking-tight">{platformName}</div>
              <div className="ros-label" style={{ fontSize: 10 }}>Multi-Specialty Console</div>
            </div>
          </div>

          {/* Middle: role-specific content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={role}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.28 }}
              className="max-w-lg relative z-10"
            >
              <div
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full border"
                style={{ color: profile.accent, borderColor: `${profile.accent}55`, background: `${profile.accent}12` }}
              >
                <Icon size={14} /> {profile.label}
              </div>
              <h1 className="font-heading text-4xl xl:text-5xl leading-[1.05] mt-5" style={{ letterSpacing: "-0.02em" }}>
                {profile.heading}
              </h1>
              <p className="text-[15px] text-[#A3ACA7] mt-4 leading-relaxed">
                {profile.tagline}
              </p>

              <ul className="mt-7 grid grid-cols-2 gap-4">
                {profile.bullets.map((b, i) => {
                  const BulletIcon = b.icon;
                  return (
                    <motion.li
                      key={b.title}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + i * 0.06 }}
                      className="flex items-start gap-3"
                      data-testid={`hero-bullet-${role}-${i}`}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                        style={{ background: `${profile.accent}14`, border: `1px solid ${profile.accent}30` }}
                      >
                        <BulletIcon size={16} style={{ color: profile.accent }} />
                      </div>
                      <div>
                        <div className="font-semibold text-white text-[13px] leading-tight">{b.title}</div>
                        <div className="text-[11.5px] text-[#A3ACA7] mt-1 leading-snug">{b.detail}</div>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            </motion.div>
          </AnimatePresence>

          {/* Footer */}
          <div className="relative z-10 text-[11px] text-[#7A8480] flex items-center gap-2">
            <ShieldCheck size={12} />
            HIPAA-grade encryption · HttpOnly cookie + Bearer dual auth · Multi-tenant B2B2C.
          </div>
        </aside>

        {/* Right — sign-in card centered, full height, no scroll */}
        <main className="h-full flex items-center justify-center px-5 sm:px-8 py-6 overflow-hidden">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md ros-card"
            data-testid="login-form-card"
          >
            {/* Role tabs */}
            <div className="flex items-center bg-[#0F1312] border border-[#272E2B] rounded-full p-1 mb-5">
              {Object.entries(PROFILES).map(([key, p]) => {
                const active = role === key;
                const RoleIcon = p.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => swapRole(key)}
                    data-testid={`role-tab-${key}`}
                    className={`flex-1 rounded-full py-2 px-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                      active ? "text-black shadow-[0_4px_18px_rgba(0,0,0,0.35)]" : "text-[#A3ACA7] hover:text-white"
                    }`}
                    style={{ background: active ? p.accent : "transparent" }}
                  >
                    <RoleIcon size={14} />
                    {key === "provider" ? "I'm a Provider" : "I'm a Patient"}
                  </button>
                );
              })}
            </div>

            {/* Mobile-only compact hero */}
            <div className="lg:hidden mb-4">
              <div
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full border"
                style={{ color: profile.accent, borderColor: `${profile.accent}55`, background: `${profile.accent}12` }}
              >
                <Icon size={11} /> {profile.label}
              </div>
              <div className="font-heading text-2xl mt-2">{profile.heading}</div>
            </div>

            <div className="flex items-baseline justify-between">
              <h2 className="font-heading text-2xl">Sign in</h2>
              <span className="text-[11px] text-[#7A8480]">{platformName}</span>
            </div>
            <p className="text-[12px] text-[#A3ACA7] mt-1">{profile.smallPrint}</p>

            <form onSubmit={handleSubmit} className="mt-4 space-y-3">
              {role === "provider" && directory.departments.length > 0 && (
                <div className="grid grid-cols-2 gap-2.5" data-testid="provider-dept-doctor-picker">
                  <div>
                    <Label className="ros-label" htmlFor="dept">Department</Label>
                    <select
                      id="dept"
                      data-testid="login-department-select"
                      value={selectedDept}
                      onChange={(e) => { setSelectedDept(e.target.value); setEmail(""); }}
                      className="mt-1.5 w-full bg-[#1C201E] border border-[#272E2B] rounded-md h-9 px-2.5 text-[13px] outline-none focus:border-[#60A5FA]"
                    >
                      <option value="">Choose department…</option>
                      {directory.departments.map((d) => (
                        <option key={d.key} value={d.key}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="ros-label" htmlFor="doctor">Doctor</Label>
                    <select
                      id="doctor"
                      data-testid="login-doctor-select"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={!selectedDept || doctorsForSelected.length === 0}
                      className="mt-1.5 w-full bg-[#1C201E] border border-[#272E2B] rounded-md h-9 px-2.5 text-[13px] outline-none focus:border-[#60A5FA] disabled:opacity-50"
                    >
                      <option value="">
                        {selectedDept ? (doctorsForSelected.length === 0 ? "No doctors yet" : "Choose doctor…") : "Pick department first"}
                      </option>
                      {doctorsForSelected.map((d) => (
                        <option key={d.id} value={d.email}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div>
                <Label className="ros-label" htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5 h-9 bg-[#1C201E] border-[#272E2B]"
                />
              </div>
              <div>
                <Label className="ros-label" htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1.5 h-9 bg-[#1C201E] border-[#272E2B]"
                />
              </div>

              {mfaRequired && (
                <div>
                  <Label className="ros-label" htmlFor="mfa">Authenticator code</Label>
                  <Input
                    id="mfa"
                    data-testid="login-mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={8}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ""))}
                    placeholder="123 456"
                    className="mt-1.5 h-9 bg-[#1C201E] border-[#272E2B] font-mono tracking-widest"
                  />
                </div>
              )}

              {error && (
                <div
                  className="text-[12px] text-[#F87171] bg-[#F87171]/10 border border-[#F87171]/20 rounded-lg p-2.5"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                data-testid="login-submit"
                className="w-full rounded-full font-semibold text-black hover:opacity-90 h-10"
                style={{ background: profile.accent }}
              >
                {submitting ? "Signing in…" : profile.cta}
              </Button>
            </form>

            {/* Demo account dropdown — compact, single row */}
            <div className="mt-4">
              <Label className="ros-label flex items-center gap-1.5" htmlFor="demo-account">
                <Sparkles size={11} /> Try a demo account
              </Label>
              <select
                id="demo-account"
                data-testid={`quick-demo-${role}`}
                value=""
                onChange={(e) => onQuickPick(e.target.value)}
                className="mt-1.5 w-full bg-[#1C201E] border border-[#272E2B] rounded-md h-9 px-2.5 text-[13px] outline-none focus:border-[#4ADE80]"
              >
                <option value="">Select a demo account…</option>
                {profile.accounts.map((a) => (
                  <option key={a.email} value={a.email}>
                    {a.label} · {a.tag}
                  </option>
                ))}
              </select>
              <div className="text-[10.5px] text-[#7A8480] mt-1.5 font-mono">Password auto-fills to <span className="text-[#A3ACA7]">demo1234</span></div>
            </div>

            {/* Cross-link */}
            <div className="mt-4 pt-4 border-t border-[#272E2B] text-[11.5px] text-[#A3ACA7]">
              {role === "provider" ? (
                <>Are you a patient?{" "}
                  <button onClick={() => swapRole("patient")} data-testid="switch-to-patient"
                          className="text-[#4ADE80] font-semibold hover:underline">
                    Switch to patient login →
                  </button>
                </>
              ) : (
                <>Are you a clinician?{" "}
                  <button onClick={() => swapRole("provider")} data-testid="switch-to-provider"
                          className="text-[#60A5FA] font-semibold hover:underline">
                    Switch to provider login →
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
}
